import { createContext, useEffect, useState } from "react";
import firebase from "firebase/compat/app";
import { onAuthStateChanged } from "firebase/auth";
import "firebase/compat/firestore";
import axios from "axios";
import { LoadingSpinner } from "../components/LoadingSpinner/LoadingSpinner";

export const AppContext = createContext({});

export const AppProvider = (props) => {
  const { children } = props;
  const [alertOptions, setAlertOptions] = useState({
    isOpen: false,
    text: "",
    status: "success",
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isVideo, setIsVideo] = useState(false);
  const [pricingOptions, setPricingOptions] = useState([]);
  const [userData, setUserData] = useState(() => {
    // Initialize userData from localStorage if available
    const savedUserData = localStorage.getItem("userData");
    return savedUserData ? JSON.parse(savedUserData) : null;
  });
  const [videoOptions, setVideoOptions] = useState({
    currentIndex: null,
    isPlaying: false,
    intervalId: null,
    startTime: null,
    elapsedTime: null,
    segmentDurations: [],
    imgPaths: [],
    musicFileUrl: null,
    subtitles: [],
    subtitleIntervalId: null,
  });

  const auth = firebase.auth();

  const handleAuthStateChange = async (user) => {
    if (user) {
      const initial = user.displayName?.charAt(0)?.toUpperCase() || "";
      setIsAuthenticated(true);
      const newUserData = {
        ...user,
        displayName: user.displayName,
        initial: initial,
      };
      setUserData(newUserData);
      localStorage.setItem("userData", JSON.stringify(newUserData));
      try {
        const idToken = await user.getIdToken(true);
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/session_login`,
          { idToken },
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        );
        const data = response.data;
        const updatedUserData = {
          ...newUserData,
          email: data.user_data?.email || null,
          credits: data.user_data?.credits || 0,
          providerId: user.providerData?.providerId || null,
          subscription_tier:
            data.user_data?.current_subscription_tier || "free",
          user_id: data.user_data?.user_id || null,
          ...data,
        };
        setUserData(updatedUserData);
        localStorage.setItem("userData", JSON.stringify(updatedUserData));
        getProducts();
      } catch (error) {
        console.error("Error during session login:", error);
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error("Response data:", error.response.data);
          console.error("Response status:", error.response.status);
          console.error("Response headers:", error.response.headers);
        } else if (error.request) {
          // The request was made but no response was received
          console.error("No response received:", error.request);
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error("Error message:", error.message);
        }
      }
    } else {
      window.history.pushState({}, "", "/");
      const navEvent = new PopStateEvent("popstate");
      window.dispatchEvent(navEvent);
      setIsAuthenticated(false);
      setUserData(null);
      localStorage.removeItem("userData");
    }
    setIsAppLoading(false);
  };
  useEffect(() => {
    setIsAppLoading(true);
    auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);

    const unsubscribe = onAuthStateChanged(auth, handleAuthStateChange);

    return () => unsubscribe();
  }, [auth]);

  async function getProducts() {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get_products`,
        {
          withCredentials: true,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      setPricingOptions(response.data[0].data);
    } catch (error) {
      console.error("Error fetching products:", error);
      // Handle the error appropriately, e.g., set an error state or show a notification
    }
  }

  const appContext = {
    alertOptions,
    auth,
    getProducts,
    isAuthenticated,
    isAppLoading,
    isLoading,
    isLoginModalOpen,
    isPricingModalOpen,
    isVideo,
    pricingOptions,
    setAlertOptions,
    setIsLoading,
    setIsLoginModalOpen,
    setIsPricingModalOpen,
    setIsVideo,
    setVideoOptions,
    userData,
    setUserData,
    videoOptions,
  };

  if (isAppLoading) {
    return <LoadingSpinner />;
  }

  return (
    <AppContext.Provider value={appContext}>{children}</AppContext.Provider>
  );
};
